/**
 * Onboarding Service
 * API client for user onboarding lifecycle management
 */
import { apiClient } from '../apiClient';

// Type Definitions

export enum OnboardingScenario {
  FIRST_TIME_USER = 'first_time_user',
  ROLE_CHANGE = 'role_change',
  CAMPUS_TRANSFER = 'campus_transfer',
  ANNUAL_UPDATE = 'annual_update'
}

export enum OnboardingStage {
  NOT_STARTED = 'not_started',
  WELCOME = 'welcome',
  FO_CONFIRMATION = 'fo_confirmation',
  PROFILE_SETUP = 'profile_setup',
  PLATFORM_TOUR = 'platform_tour',
  OBJECTIVES_PLANNING = 'objectives_planning',
  COMPLETED = 'completed'
}

export interface OnboardingStatus {
  user_id: number;
  onboarding_scenario: string;
  current_stage: string;
  profile_completed: boolean;
  profile_completion_percentage: number;
  has_objectives: boolean;
  fo_data_confirmed: boolean;
  fo_data_correct: boolean | null;
  manager_assigned: boolean;
  tour_completed: boolean;
  onboarding_completed_at: string | null;
  collaborative_session_scheduled: boolean;
  collaborative_session_date: string | null;
  hr_escalation_required: boolean;
  completed_stages: Record<string, string> | null;
  annual_update_due_date: string | null;
  annual_update_overdue: boolean;
}

export interface OnboardingTask {
  id: number;
  task_type: string;
  task_title: string;
  task_description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed' | 'overdue' | 'skipped';
  due_date: string | null;
  action_url: string | null;
  action_required_from: string | null;
  is_overdue: boolean;
  created_at: string;
}

export interface OnboardingNotification {
  id: number;
  notification_type: string;
  notification_title: string;
  notification_message: string;
  notification_channel: string;
  sent_at: string | null;
  read_at: string | null;
  is_read: boolean;
  action_required: boolean;
  action_url: string | null;
  created_at: string;
}

export interface ProfileSection {
  section: 'qualifications' | 'previous_roles' | 'professional_context';
  completed: boolean;
  data?: any;
}

export interface FOConfirmation {
  data_correct: boolean;
  escalation_reason?: string;
  hr_ticket?: {
    ticketId: string;
    employeeId: string;
    employeeName: string;
    issueType: string;
    affectedFields: string[];
    userDescription: string;
    source: string;
    description: string;
    status: string;
    createdAt: string;
  };
}

export interface CollaborativeSession {
  session_date: string;
}

// HR Ticket Types
export interface HRTicketResponse {
  id: number;
  ticket_id: string;
  employee_user_id: number;
  employee_name: string;
  employee_id?: string;
  employee_email?: string;
  functional_area?: string;
  issue_type: string;
  source: string;
  affected_fields?: string[];
  user_description?: string;
  description: string;
  status: string;
  assigned_to_user_id?: number;
  assigned_to_name?: string;
  resolution_notes?: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
  // Enriched profile fields for HR reference
  employee_campus?: string;
  employee_office_location?: string;
  employee_job_title?: string;
  employee_position_type?: string;
  employee_faculty?: string;
  employee_faculty_name?: string;
  employee_department?: string;
  employee_manager_name?: string;
  employee_has_direct_reports?: boolean;
}

export interface HRTicketListResponse {
  tickets: HRTicketResponse[];
  total: number;
  pending_count: number;
  in_progress_count: number;
  resolved_count: number;
  field_counts: Record<string, number>;
  unique_functional_areas: string[];
}

export interface HRTicketUpdateRequest {
  status: string;
  resolution_notes?: string;
  assigned_to_name?: string;
  assigned_to_user_id?: number;
}

export interface TicketAssignee {
  user_id: number;
  display_name: string;
  email: string;
}

export interface TicketMetrics {
  avg_turnaround_hours: number | null;
  avg_time_to_claim_hours: number | null;
  unassigned_count: number;
  oldest_pending_hours: number | null;
}

// API Service Class

class OnboardingService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = '/identity/onboarding';
  }

  /**
   * Get current user's onboarding status
   */
  async getStatus(): Promise<OnboardingStatus> {
    try {
      const response = await apiClient.get<OnboardingStatus>(`${this.baseUrl}/status`);
      return response.data;
    } catch (error) {
      console.error('Failed to get onboarding status:', error);
      throw error;
    }
  }

  /**
   * Initialize onboarding for a user
   */
  async initialize(scenario: OnboardingScenario = OnboardingScenario.FIRST_TIME_USER): Promise<OnboardingStatus> {
    try {
      const response = await apiClient.post<OnboardingStatus>(`${this.baseUrl}/initialize`, null, {
        params: { scenario }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to initialize onboarding:', error);
      throw error;
    }
  }

  /**
   * Update onboarding stage
   */
  async updateStage(newStage: OnboardingStage): Promise<OnboardingStatus> {
    try {
      const response = await apiClient.post<OnboardingStatus>(`${this.baseUrl}/stage/update`, null, {
        params: { new_stage: newStage }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update onboarding stage:', error);
      throw error;
    }
  }

  /**
   * Confirm F&O data accuracy
   */
  async confirmFOData(confirmation: FOConfirmation): Promise<OnboardingStatus> {
    try {
      const response = await apiClient.post<OnboardingStatus>(`${this.baseUrl}/fo-confirmation`, confirmation);
      return response.data;
    } catch (error) {
      console.error('Failed to confirm F&O data:', error);
      throw error;
    }
  }

  /**
   * Update profile section
   */
  async updateProfileSection(section: ProfileSection): Promise<OnboardingStatus> {
    try {
      const response = await apiClient.post<OnboardingStatus>(`${this.baseUrl}/profile/section`, section);
      return response.data;
    } catch (error) {
      console.error('Failed to update profile section:', error);
      throw error;
    }
  }

  /**
   * Complete platform tour
   */
  async completeTour(skipped: boolean = false): Promise<OnboardingStatus> {
    try {
      const response = await apiClient.post<OnboardingStatus>(`${this.baseUrl}/tour/complete`, null, {
        params: { skipped }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to complete tour:', error);
      throw error;
    }
  }

  /**
   * Schedule collaborative objective-setting session
   */
  async scheduleSession(sessionData: CollaborativeSession): Promise<OnboardingStatus> {
    try {
      const response = await apiClient.post<OnboardingStatus>(`${this.baseUrl}/session/schedule`, sessionData);
      return response.data;
    } catch (error) {
      console.error('Failed to schedule session:', error);
      throw error;
    }
  }

  /**
   * Complete onboarding
   */
  async complete(): Promise<OnboardingStatus> {
    try {
      const response = await apiClient.post<OnboardingStatus>(`${this.baseUrl}/complete`);
      return response.data;
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      throw error;
    }
  }

  /**
   * Get user's onboarding tasks
   */
  async getTasks(): Promise<OnboardingTask[]> {
    try {
      const response = await apiClient.get<OnboardingTask[]>(`${this.baseUrl}/tasks`);
      return response.data;
    } catch (error) {
      console.error('Failed to get onboarding tasks:', error);
      throw error;
    }
  }

  /**
   * Get user's onboarding notifications
   */
  async getNotifications(unreadOnly: boolean = false): Promise<OnboardingNotification[]> {
    try {
      const response = await apiClient.get<OnboardingNotification[]>(`${this.baseUrl}/notifications`, {
        params: { unread_only: unreadOnly }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get onboarding notifications:', error);
      throw error;
    }
  }

  /**
   * Trigger annual profile update
   */
  async triggerAnnualUpdate(): Promise<OnboardingStatus> {
    try {
      const response = await apiClient.post<OnboardingStatus>(`${this.baseUrl}/annual-update/trigger`);
      return response.data;
    } catch (error) {
      console.error('Failed to trigger annual update:', error);
      throw error;
    }
  }

  /**
   * Check if user needs onboarding
   */
  async needsOnboarding(): Promise<boolean> {
    try {
      const status = await this.getStatus();
      return status.current_stage !== OnboardingStage.COMPLETED;
    } catch (error) {
      // If status doesn't exist, user needs onboarding
      return true;
    }
  }

  /**
   * Get onboarding progress percentage
   */
  getProgressPercentage(status: OnboardingStatus): number {
    if (status.current_stage === OnboardingStage.COMPLETED) return 100;
    
    const stages = [
      OnboardingStage.NOT_STARTED,
      OnboardingStage.WELCOME,
      OnboardingStage.FO_CONFIRMATION,
      OnboardingStage.PROFILE_SETUP,
      OnboardingStage.PLATFORM_TOUR,
      OnboardingStage.OBJECTIVES_PLANNING,
      OnboardingStage.COMPLETED
    ];
    
    const currentIndex = stages.indexOf(status.current_stage as OnboardingStage);
    return Math.round((currentIndex / (stages.length - 1)) * 100);
  }

  /**
   * Get human-readable stage name
   */
  getStageName(stage: string): string {
    const stageNames: Record<string, string> = {
      [OnboardingStage.NOT_STARTED]: 'Not Started',
      [OnboardingStage.WELCOME]: 'Welcome',
      [OnboardingStage.FO_CONFIRMATION]: 'F&O Data Confirmation',
      [OnboardingStage.PROFILE_SETUP]: 'Profile Setup',
      [OnboardingStage.PLATFORM_TOUR]: 'Platform Tour',
      [OnboardingStage.OBJECTIVES_PLANNING]: 'Objectives Planning',
      [OnboardingStage.COMPLETED]: 'Completed'
    };
    
    return stageNames[stage] || stage;
  }

  /**
   * Get scenario display name
   */
  getScenarioName(scenario: string): string {
    const scenarioNames: Record<string, string> = {
      [OnboardingScenario.FIRST_TIME_USER]: 'First-Time User',
      [OnboardingScenario.ROLE_CHANGE]: 'Role Change',
      [OnboardingScenario.CAMPUS_TRANSFER]: 'Campus Transfer',
      [OnboardingScenario.ANNUAL_UPDATE]: 'Annual Update'
    };
    
    return scenarioNames[scenario] || scenario;
  }

  // ================================
  // HR Data Correction Ticket Methods
  // ================================

  /**
   * List HR data correction tickets
   */
  async listHRTickets(params?: {
    status?: string;
    source?: string;
    affected_fields?: string;
    search?: string;
    functional_area?: string;
    assigned_to?: string;
    skip?: number;
    limit?: number;
  }): Promise<HRTicketListResponse> {
    try {
      const response = await apiClient.get<HRTicketListResponse>(`${this.baseUrl}/hr-tickets`, {
        params: {
          status_filter: params?.status,
          source_filter: params?.source,
          affected_fields: params?.affected_fields || undefined,
          search: params?.search || undefined,
          functional_area: params?.functional_area || undefined,
          assigned_to: params?.assigned_to || undefined,
          skip: params?.skip || 0,
          limit: params?.limit || 100
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to list HR tickets:', error);
      throw error;
    }
  }

  /**
   * Get a specific HR ticket
   */
  async getHRTicket(ticketId: string): Promise<HRTicketResponse> {
    try {
      const response = await apiClient.get<HRTicketResponse>(`${this.baseUrl}/hr-tickets/${ticketId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get HR ticket:', error);
      throw error;
    }
  }

  /**
   * Update HR ticket status
   */
  async updateHRTicket(ticketId: string, update: HRTicketUpdateRequest): Promise<HRTicketResponse> {
    try {
      const response = await apiClient.patch<HRTicketResponse>(`${this.baseUrl}/hr-tickets/${ticketId}`, update);
      return response.data;
    } catch (error) {
      console.error('Failed to update HR ticket:', error);
      throw error;
    }
  }

  /**
   * Get ticket metrics (turnaround times, unassigned count, etc.)
   */
  async getTicketMetrics(): Promise<TicketMetrics> {
    try {
      const response = await apiClient.get<TicketMetrics>(`${this.baseUrl}/hr-tickets-metrics`);
      return response.data;
    } catch (error) {
      console.error('Failed to get ticket metrics:', error);
      throw error;
    }
  }

  /**
   * Get list of users eligible to be assigned tickets
   */
  async getTicketAssignees(): Promise<TicketAssignee[]> {
    try {
      const response = await apiClient.get<TicketAssignee[]>(`${this.baseUrl}/hr-tickets-assignees`);
      return response.data;
    } catch (error) {
      console.error('Failed to get ticket assignees:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const onboardingService = new OnboardingService();
export default onboardingService;
